// src/pages/Performance/WorkflowStepsCard.jsx
import React, { useEffect, useMemo, useRef } from "react";
import {
  Box,
  CardHeader,
  CardContent,
  Typography,
  Stack,
  Button,
  Tooltip,
  Avatar,
  Chip,
  CircularProgress,
  Fade,
  Paper,
  useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PersonIcon from "@mui/icons-material/Person";
import PendingOutlinedIcon from "@mui/icons-material/PendingOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";

import {
  GlassCard,
  AccentBar,
  StepPill,
  TimelineConnector,
  ProgressIndicator,
} from "./evaluateAppraisal.styled";
import { getStepLabel, STEP_STATUS_COLOR } from "./evaluateAppraisal.utils";

const STEP_STATUS_ICON = {
  completed: <CheckCircleIcon fontSize="small" sx={{ color: "success.main" }} />,
  submitted: <CheckCircleIcon fontSize="small" sx={{ color: "info.main" }} />,
  in_progress: <CircularProgress size={12} sx={{ color: "warning.main" }} />,
  pending: <PendingOutlinedIcon fontSize="small" sx={{ color: "warning.main" }} />,
  not_started: <VisibilityIcon fontSize="small" sx={{ color: "text.disabled" }} />,
};

function prettyStatus(s) {
  return String(s || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

export default function WorkflowStepsCard({
  stepsTimeline = [],
  selectedStep,
  appraisal,
  onSelectStep,
  onExpandAll,
  onCollapseAll,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  // show connectors only when it will clearly fit (keeps same look without forcing horizontal scroll)
  const showConnectors = isMdUp && stepsTimeline.length <= 5;

  const wrapRef = useRef(null);

  const { completedCount, progressPct } = useMemo(() => {
    const done = stepsTimeline.filter((s) => s.status === "completed").length;
    const pct = stepsTimeline.length ? Math.round((done / stepsTimeline.length) * 100) : 0;
    return { completedCount: done, progressPct: pct };
  }, [stepsTimeline]);

  useEffect(() => {
    if (!wrapRef.current) return;
    if (!selectedStep?.id) return;

    const el = wrapRef.current.querySelector(`[data-step-id="${selectedStep.id}"]`);
    if (!el) return;

    // vertical scroll only (no horizontal page scrolling)
    el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
  }, [selectedStep?.id]);

  const getStepBorderColor = (step, isActive) => {
    if (isActive) return theme.palette.primary.main;
    if (step.status === "completed") return theme.palette.success.main;
    if (step.status === "in_progress") return theme.palette.warning.main;
    if (step.status === "submitted") return theme.palette.info.main;
    return theme.palette.divider;
  };

  // ---- shared little presentational helpers (no logic, just visuals) ----
  const expandCollapseBtnSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 800,
    borderWidth: 1.5,
    px: { xs: 1.25, md: 1.75 },
    bgcolor: alpha(theme.palette.background.paper, 0.6),
    backdropFilter: "blur(6px)",
    transition: "border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease",
    "&:hover": {
      borderWidth: 1.5,
      transform: "translateY(-1px)",
      boxShadow: `0 6px 14px ${alpha(theme.palette.primary.main, 0.14)}`,
    },
  };

  return (
    <GlassCard>
      <Box sx={{ px: 2.5, pt: 2.25, pb: 0.5 }}>
        <AccentBar />
      </Box>

      <CardHeader
        title={
          <Stack spacing={0.6}>
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Avatar
                variant="rounded"
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: "12px",
                  color: theme.palette.primary.main,
                  background: `linear-gradient(135deg, ${alpha(
                    theme.palette.primary.main,
                    0.16
                  )} 0%, ${alpha(theme.palette.secondary.main, 0.12)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
                  boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.12)}`,
                }}
              >
                <RouteOutlinedIcon fontSize="small" />
              </Avatar>

              <Stack spacing={0.1} sx={{ minWidth: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.15 }}>
                  Workflow Steps
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Select a step to view its details.
                </Typography>
              </Stack>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap", pl: 0.25 }}>
              <Chip
                size="small"
                icon={<CheckCircleIcon sx={{ fontSize: 15 }} />}
                label={`${completedCount}/${stepsTimeline.length} completed`}
                sx={{
                  height: 26,
                  fontWeight: 800,
                  color: theme.palette.success.main,
                  "& .MuiChip-icon": { color: theme.palette.success.main, ml: 0.5 },
                  border: `1px solid ${alpha(theme.palette.success.main, 0.28)}`,
                  bgcolor: alpha(theme.palette.success.main, 0.08),
                }}
              />
              <Chip
                size="small"
                variant="outlined"
                label={`${progressPct}% progress`}
                sx={{
                  height: 26,
                  fontWeight: 800,
                  borderColor: alpha(theme.palette.primary.main, 0.26),
                  color: theme.palette.primary.main,
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                }}
              />
            </Stack>
          </Stack>
        }
        action={
          <Fade in>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ pt: 0.5 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={onExpandAll}
                startIcon={<ExpandMoreIcon />}
                sx={expandCollapseBtnSx}
              >
                Expand
              </Button>

              <Button
                size="small"
                variant="outlined"
                onClick={onCollapseAll}
                startIcon={<ExpandLessIcon />}
                sx={expandCollapseBtnSx}
              >
                Collapse
              </Button>
            </Stack>
          </Fade>
        }
        sx={{
          pb: 1.5,
          alignItems: "flex-start",
          "& .MuiCardHeader-action": { alignSelf: "flex-start", mt: 0, mr: 0 },
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      />

      <CardContent sx={{ pt: 1.5, px: { xs: 1.25, sm: 2 }, overflowX: "hidden" }}>
        {/* No horizontal scrolling: wrap on desktop, vertical list on mobile. */}
        {isMobile ? (
          <Stack spacing={1.25} ref={wrapRef}>
            {stepsTimeline.map((step) => {
              const isActive = Number(selectedStep?.id) === Number(step.id);
              const label = getStepLabel(step);
              const assignee = step.user?.name || "-";
              const isMyStep = Number(appraisal?.my_pending_step_id) === Number(step.id);
              const isDone = step.status === "completed";
              const borderColor = getStepBorderColor(step, isActive);

              return (
                <Paper
                  key={step.id}
                  data-step-id={step.id}
                  variant="outlined"
                  onClick={() => onSelectStep(step.id)}
                  sx={{
                    position: "relative",
                    p: 1.25,
                    pl: 1.5,
                    cursor: "pointer",
                    borderRadius: "12px",
                    overflow: "hidden",
                    borderColor: alpha(borderColor, isActive ? 0.55 : 0.22),
                    bgcolor: isActive
                      ? alpha(theme.palette.primary.main, 0.06)
                      : alpha(theme.palette.background.paper, 0.65),
                    transition: "border-color 180ms ease, background 180ms ease, box-shadow 180ms ease",
                    boxShadow: isActive
                      ? `0 8px 18px ${alpha(theme.palette.primary.main, 0.16)}`
                      : "none",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: 4,
                      background: `linear-gradient(to bottom, ${alpha(borderColor, isActive ? 0.9 : 0.5)}, ${alpha(
                        borderColor,
                        isActive ? 0.5 : 0.25
                      )})`,
                    },
                    "&:hover": {
                      borderColor: alpha(borderColor, 0.5),
                    },
                  }}
                >
                  <Stack spacing={0.85}>
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Avatar
                        sx={{
                          width: 30,
                          height: 30,
                          flexShrink: 0,
                          bgcolor: isActive
                            ? theme.palette.primary.main
                            : isDone
                              ? theme.palette.success.main
                              : alpha(theme.palette.primary.main, 0.1),
                          color: isActive
                            ? theme.palette.primary.contrastText
                            : isDone
                              ? theme.palette.success.contrastText
                              : theme.palette.primary.main,
                          fontSize: 13,
                          fontWeight: 900,
                          border: `2px solid ${alpha(borderColor, 0.55)}`,
                        }}
                      >
                        {isDone ? <CheckCircleIcon sx={{ fontSize: 18 }} /> : step.step_order}
                      </Avatar>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 800,
                            color: isActive ? theme.palette.primary.main : theme.palette.text.primary,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {step.step_order}. {label}
                        </Typography>
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                          <PersonIcon sx={{ fontSize: 13, color: "text.disabled", flexShrink: 0 }} />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                          >
                            {assignee}
                          </Typography>
                        </Stack>
                      </Box>

                      <Chip
                        size="small"
                        label={prettyStatus(step.status)}
                        color={STEP_STATUS_COLOR[step.status] || "default"}
                        icon={STEP_STATUS_ICON[step.status] || <EditIcon fontSize="small" />}
                        sx={{ height: 24, fontSize: "0.72rem", fontWeight: 800, flexShrink: 0 }}
                      />
                    </Stack>

                    {isMyStep && (
                      <Chip
                        size="small"
                        color="primary"
                        icon={<PlayCircleOutlineIcon fontSize="small" />}
                        label="Your Task"
                        sx={{
                          height: 24,
                          fontSize: "0.72rem",
                          fontWeight: 900,
                          alignSelf: "flex-start",
                          boxShadow: `0 4px 10px ${alpha(theme.palette.primary.main, 0.22)}`,
                        }}
                      />
                    )}
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        ) : (
          <Box
            ref={wrapRef}
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "stretch",
              gap: 1.25,
              // prevents any accidental page horizontal scroll
              maxWidth: "100%",
              overflowX: "hidden",
            }}
          >
            {stepsTimeline.map((step, index) => {
              const isActive = Number(selectedStep?.id) === Number(step.id);
              const label = getStepLabel(step);
              const assignee = step.user?.name || "-";
              const isMyStep = Number(appraisal?.my_pending_step_id) === Number(step.id);
              const isDone = step.status === "completed";

              const borderColor = getStepBorderColor(step, isActive);

              return (
                <React.Fragment key={step.id}>
                  <Box
                    data-step-id={step.id}
                    sx={{
                      flex: "1 1 260px", // wraps nicely without horizontal scroll
                      maxWidth: 380,
                      minWidth: 260,
                    }}
                  >
                    <Tooltip
                      arrow
                      placement="top"
                      title={
                        <Paper elevation={6} sx={{ p: 1.25, maxWidth: 340, borderRadius: "8px" }}>
                          <Stack spacing={0.75}>
                            <Typography variant="subtitle2" fontWeight={900} noWrap>
                              {label}
                            </Typography>

                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <PersonIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                              <Typography variant="caption" color="text.secondary">
                                Assignee:
                              </Typography>
                              <Typography variant="caption" fontWeight={800}>
                                {assignee}
                              </Typography>
                            </Stack>

                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: "wrap" }}>
                              <Chip
                                size="small"
                                label={prettyStatus(step.status)}
                                color={STEP_STATUS_COLOR[step.status] || "default"}
                                icon={STEP_STATUS_ICON[step.status] || <EditIcon fontSize="small" />}
                                sx={{ height: 22, fontSize: "0.7rem", fontWeight: 800 }}
                              />
                              {isMyStep && (
                                <Chip
                                  size="small"
                                  color="primary"
                                  icon={<PlayCircleOutlineIcon fontSize="small" />}
                                  label="Your Task"
                                  sx={{ height: 22, fontSize: "0.7rem", fontWeight: 900 }}
                                />
                              )}
                            </Stack>
                          </Stack>
                        </Paper>
                      }
                    >
                      <Box
                        role="button"
                        tabIndex={0}
                        onClick={() => onSelectStep(step.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            onSelectStep(step.id);
                          }
                        }}
                        sx={{
                          position: "relative",
                          height: "100%",
                          cursor: "pointer",
                          borderRadius: "12px",
                          outline: "none",
                          p: 0.25,
                          transition: "transform 180ms ease, box-shadow 180ms ease",
                          "&:hover": {
                            transform: "translateY(-2px)",
                          },
                          "&:hover .step-glow": {
                            opacity: 1,
                          },
                          "&:focus-visible": {
                            boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.18)}`,
                          },
                        }}
                      >
                        {/* soft glow behind active / hovered pill */}
                        <Box
                          className="step-glow"
                          sx={{
                            position: "absolute",
                            inset: 0,
                            borderRadius: "12px",
                            pointerEvents: "none",
                            opacity: isActive ? 1 : 0,
                            transition: "opacity 180ms ease",
                            boxShadow: `0 10px 26px ${alpha(theme.palette.primary.main, 0.18)}`,
                          }}
                        />

                        <StepPill
                          active={isActive ? 1 : 0}
                          label={`${step.step_order}. ${label}`}
                          icon={
                            <Avatar
                              sx={{
                                width: 30,
                                height: 30,
                                bgcolor: isActive
                                  ? theme.palette.primary.main
                                  : isDone
                                    ? theme.palette.success.main
                                    : alpha(theme.palette.primary.main, 0.1),
                                color: isActive
                                  ? theme.palette.primary.contrastText
                                  : isDone
                                    ? theme.palette.success.contrastText
                                    : theme.palette.primary.main,
                                fontSize: 14,
                                fontWeight: 900,
                                border: `2px solid ${alpha(borderColor, 0.55)}`,
                                transition: "all 180ms ease",
                              }}
                            >
                              {isDone ? <CheckCircleIcon sx={{ fontSize: 18 }} /> : step.step_order}
                            </Avatar>
                          }
                          sx={{
                            position: "relative",
                            width: "100%",
                            px: 2.25,
                            py: 1.25,
                            borderColor: alpha(borderColor, isActive ? 0.5 : 0.2),
                            "& .MuiChip-label": {
                              maxWidth: "calc(100% - 52px)",
                            },
                            "&:hover": {
                              borderColor: alpha(theme.palette.primary.main, 0.35),
                            },
                          }}
                        />

                        <Stack
                          direction="row"
                          spacing={0.75}
                          alignItems="center"
                          justifyContent="center"
                          sx={{ mt: 0.9, flexWrap: "wrap" }}
                        >
                          <Chip
                            size="small"
                            label={prettyStatus(step.status)}
                            color={STEP_STATUS_COLOR[step.status] || "default"}
                            icon={STEP_STATUS_ICON[step.status] || <EditIcon fontSize="small" />}
                            sx={{ height: 24, fontSize: "0.72rem", fontWeight: 800 }}
                          />
                          {isMyStep && (
                            <Chip
                              size="small"
                              color="primary"
                              icon={<PlayCircleOutlineIcon fontSize="small" />}
                              label="You"
                              sx={{
                                height: 24,
                                fontSize: "0.72rem",
                                fontWeight: 900,
                                boxShadow: `0 4px 10px ${alpha(theme.palette.primary.main, 0.22)}`,
                              }}
                            />
                          )}
                        </Stack>
                      </Box>
                    </Tooltip>
                  </Box>

                  {/* Keep same connector style only when it will fit nicely */}
                  {showConnectors && index < stepsTimeline.length - 1 && (
                    <TimelineConnector
                      active={Number(selectedStep?.id) === Number(step.id) ? 1 : 0}
                      completed={step.status === "completed" ? 1 : 0}
                      sx={{
                        flexShrink: 0,
                        mx: 0.25,
                        alignSelf: "center",
                      }}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </Box>
        )}

        {/* Progress */}
        <Box
          sx={{
            mt: 2.5,
            px: { xs: 1.25, md: 1.75 },
            py: 1.5,
            borderRadius: "12px",
            border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.primary.main,
              0.05
            )} 0%, ${alpha(theme.palette.secondary.main, 0.04)} 100%)`,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={{ xs: 1, sm: 1.5 }}
          >
            <Stack spacing={0.9} sx={{ flexGrow: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.6 }}
                >
                  Overall progress
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                  {completedCount} of {stepsTimeline.length} steps completed
                </Typography>
              </Stack>

              <ProgressIndicator progress={progressPct} />
            </Stack>

            <Box
              sx={{
                flexShrink: 0,
                alignSelf: { xs: "flex-end", sm: "center" },
                px: 1.5,
                py: 0.6,
                borderRadius: "999px",
                fontWeight: 900,
                color: theme.palette.primary.main,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.24)}`,
                minWidth: 56,
                textAlign: "center",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 900, lineHeight: 1 }}>
                {progressPct}%
              </Typography>
            </Box>
          </Stack>
        </Box>
      </CardContent>
    </GlassCard>
  );
}
